import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import Analytics from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';

const Signup = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get('redirect');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: ''
    });
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Client-side validation
        if (!formData.name.trim() || formData.name.trim().length < 2) {
            setError('Full name is required (at least 2 characters).');
            return;
        }
        if (!formData.email.trim()) {
            setError('Email address is required.');
            return;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!formData.password) {
            setError('Password is required.');
            return;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        try {
            const response = await api.post('/api/auth/signup', formData);
            if (response.ok) {
                const data = await response.json();

                // Auto-login: backend now returns token + user on signup
                if (data.token && data.user) {
                    login(data.token, data.user);

                    // Analytics: Track standard sign up event
                    Analytics.pushCustomEvent('sign_up', { method: 'email' });

                    if (redirectTo) {
                        navigate(redirectTo);
                    } else {
                        navigate('/dashboard');
                    }
                } else {
                    // Fallback: backend didn't return token, redirect to login
                    const loginPath = redirectTo
                        ? `/login?redirect=${encodeURIComponent(redirectTo)}`
                        : '/login';
                    navigate(loginPath);
                }
            } else {
                const data = await response.json();
                setError(data.message || 'Signup failed. Please try again.');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setError('');
        const inviteToken = searchParams.get('token');
        try {
            const response = await api.post('/api/auth/google', {
                credential: credentialResponse.credential,
                inviteToken: inviteToken
            });
            if (response.ok) {
                const data = await response.json();
                login(data.token, data.user);

                // Analytics: Track standard sign up event
                Analytics.pushCustomEvent('sign_up', { method: 'google' });

                if (redirectTo) {
                    navigate(redirectTo);
                } else if (data.user.role === "ARTISAN") {
                    navigate('/artisan/dashboard');
                } else {
                    navigate('/dashboard');
                }
            } else {
                const data = await response.json();
                setError(data.message || 'Google signup failed.');
            }
        } catch (err) {
            setError('Google signup failed. Please try again.');
        }
    };

    // Build login link preserving redirect param
    const loginLink = redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login';


    return (
        <div className="auth-container">
            <div className="auth-box fade-in-section in-view">
                <h2>Create Account</h2>
                <p className="auth-subtitle">Join Glaminant today</p>

                {redirectTo && (
                    <div className="auth-info-banner">
                        ✨ Create an account to complete your booking
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* Google login hidden due to key issue - keeping code for future use */
                    false && (
                        <>
                            <div className="social-auth-container">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError('Google Signup Failed')}
                                    useOneTap
                                    theme="filled_blue"
                                    shape="pill"
                                    width="100%"
                                    text="signup_with"
                                />
                            </div>

                            <div className="auth-separator">
                                <span>or continue with email</span>
                            </div>
                        </>
                    )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Full Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Doe"
                        />
                    </div>

                    <div className="form-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="name@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Create a password"
                        />
                    </div>

                    <div className="form-group">
                        <label>Phone Number</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                        />
                    </div>

                    <div className="form-group">
                        <label>Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="123 Main St, City, State"
                        />
                    </div>

                    <button type="submit" className="btn-primary full-width">Sign Up</button>
                </form>

                <p className="auth-footer">
                    Already have an account? <Link to={loginLink}>Login</Link>
                </p>
            </div>
        </div>
    );
};

export default Signup;
